import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { HwTypeKey } from '@const/hwTypeKey.constants';
import { IHwTypeBase } from '@dto/architecture.dto';
import { Export } from '@dto/pageResponse.dto';
import { BaseDeviceProps } from '@hostcfgs/deviceProps';
import { ValidateErrorEntity } from '@rc-component/form/es/interface';
import { Button, Card, Checkbox, Form, FormInstance, Input, Popconfirm } from 'antd';
import { FormItemProps, Rule, RuleObject } from 'antd/es/form';
import { NamePath, StoreValue } from 'antd/es/form/interface';
import { debounce } from 'lodash-es';
import { Component, ReactNode, createRef } from 'react';
import { useAppGlobStore } from 'src/appGlobalStore';
import { ColProps, GridProps } from "src/component/grid/gridTypes";
import { XmHelp } from 'src/component/help/XmHelp';
import { XmModal } from "src/component/modal/XmModal";
import XmTableUtil from 'src/component/tableUtil/XmTableUtil';
import { Const } from 'src/data/Const';
import { Label } from 'src/data/Label';
import { DataService } from 'src/service/data.service';
import { TextUtil } from 'src/util/TextUtil';
import { nameof } from 'ts-simple-nameof';
import { TableTooltip } from 'src/component/grid/Grid';
import { BasicFormProps, FormValidateMessages, HostCfgCardProps } from 'src/data/props';
import { Log } from 'src/service/log.service';
import { Deferred } from 'src/util/Deferred';
import { GLOB } from 'src/util/Glob';
import { ImportConnectionTest } from '../../../configuration/exportImport/importRRD/components/importConnectionTest';
import AdvancedSettingsCFG from '../AdvancedSettingsCFG';
import {
  createHostCfg,
  deleteHostCfg,
  fetchData,
  getHostCfgList,
  getVendors,
  updateHostCfg
} from '../ConfigurationService';
import { HostCfgItem } from '../ConfigurationTypes';
import { ConnectionTestModal } from '../ConnectionTest';
import '../HostCfg.less';
import TableConfiguration from '../TableConfiguration';
import { AbstractDevice } from '../stor/AbstractDevice';
import CustomLinksFormList from "../../../tools/cmdb/customLinks/CustomLinksFormList";
import CustomLink from "../../../tools/cmdb/customLinks/CustomLink";
import { ItemCustomLink } from "@dto/customLinks.dto";

export interface PlatformState<P extends BaseDeviceProps, S> {
  data: HostCfgItem<P>[];
  dialog: Partial<S>;
  cfgInEdit: HostCfgItem<P>;
  modalVisible: boolean;
  actionName: string;
  timestamp: number;
  saving: boolean;
  canAddDevice: boolean;
  key?: string;
  useReset?: boolean;
  export?: Export;
  proxyColumn: boolean;
  isModalOpen?: boolean;
  record?: HostCfgItem<P>;
  isConnTestOpen?: boolean;
}

export abstract class AbstractPlatform<P extends BaseDeviceProps, S = Record<string, unknown>, T extends PlatformState<P, S> = PlatformState<P, S>>
  extends Component<unknown, T> {

  private readonly columns: ColProps<HostCfgItem<P>>[] = ([
    {
      title: 'Name',
      key: 'hostalias',
      filter: { field: ['label'] },
      sorter: (a, b) => GLOB.naturalSort(a.label, b.label),
      render: (value, record) => ((!record.target)
        ? <a
          className='d-flex hover-info'
          title={record.data.description}
          onClick={() => this.showModalForm(record, 'Edit')}
        >
          {record.label}
        </a>
        : <span className='d-flex' title={record.data.description}>{record.label}</span>
      ),
    },
    {
      title: 'Hostname',
      key: 'host',
      width: '20%',
      render: (value, record) => {
        const hosts = this.parseHosts(record.data);
        return hosts.join(', ');
      },
    },
    {
      title: 'Proxy',
      key: 'proxy',
      width: '4em',
      render: (value, record) =>
        (record.target === true ? record.data.sourceAddress : "")
    },
    {
      title: 'Test',
      key: 'test',
      width: '4em',
      render: (value, record) => (
        <Button
          type='text'
          className='hover-info text-center w-100'
          icon={<PlayCircleOutlined />}
          title='Test connection'
          onClick={() => this.testCon(record)}
          disabled={record.target || useAppGlobStore.getState().info?.backend?.demo}
        />
      ),
    },
    !GLOB.isProdEnv() && {
      title: 'Fetch',
      key: 'fetch',
      width: '4em',
      render: (value, record) => (
        <Button
          type='text'
          className='hover-info text-center w-100'
          icon={<PlayCircleOutlined />}
          title="Fetch data"
          onClick={() => fetchData(record)}
          disabled={record.target}
        />
      )
    },
    {
      title: 'Mgmt',
      key: 'management',
      width: '4em',
      render: (value, record) =>
        record.data.managementUrl ? (
          <Button
            type='text'
            className='hover-info text-center w-100'
            icon={<ExportOutlined />}
            title='Management'
            onClick={() => window.open(record.data.managementUrl, '_blank')}
          />
        ) : null,
    },
    {
      title: 'Edit',
      key: 'edit',
      width: '3em',
      render: (value, record) => (
        <Button
          type='text'
          className='hover-info text-center w-100'
          icon={<EditOutlined />}
          title='Edit configuration'
          onClick={() => this.showModalForm(record, 'Edit')}
          disabled={record.target}
        />
      ),
    },
    {
      title: 'Clone',
      key: 'clone',
      width: '4em',
      render: (value, record) => (
        <Button
          type='text'
          className='hover-info text-center w-100'
          icon={<CopyOutlined />}
          title='Clone configuration'
          onClick={() =>
            this.showModalForm({ ...record, hostcfg_id: null, source: record.hostcfg_id }, 'Clone')
          }
          disabled={record.target}
        />
      ),
    },
    {
      title: 'Delete',
      key: 'delete',
      width: '4em',
      render: (value, record) =>
        <Popconfirm
          okText='Delete'
          title={
            <span style={{ marginLeft: '0.5em' }}>
              Delete <strong>{record.label}</strong> configuration? <br />
              {record.target && <span style={{ color: 'red' }}>This configuration is from target proxy! Device should be deleted on target!</span>}
            </span>
          }
          onConfirm={() => this.deleteCfg(record)}
          icon={<WarningOutlined style={{ fontSize: '1.5em', top: '0.25em' }} />}
        >
          <Button
            type='text'
            className='hover-danger text-center w-100'
            icon={<DeleteOutlined />}
            title='Delete configuration'
          />
        </Popconfirm>
      ,
    },
    {
      key: 'advanced',
      title: 'Advanced',
      render: (_, record) => (
        <Button
          type='text'
          className='hover-info w-100'
          icon={<SettingOutlined />}
          title='Advanced settings'
          onClick={() => {
            this.setIsModalOpen(true);
            this.setRecord(record);
          }}
          disabled={record.target}
        />
      )
    },
    {
      key: 'links',
      title: 'Links',
      render: (_, record) => (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', alignItems: 'flex-start' }}>
          {(record.custom_links as Omit<ItemCustomLink, 'item'>[] | undefined)?.map(link => <CustomLink key={link.id} {...link} />)}
        </span>
      ),
    },
    {
      key: 'description',
      title: 'Description',
      render: (value, record) => {
        return <TableTooltip title={record.data.description}>{TextUtil.trimEnd(record.data.description, 64)}</TableTooltip>;
      }
    },
  ] as ColProps<HostCfgItem<P>>[]).filter(Boolean);

  protected readonly abortCtrl = new AbortController();
  private readonly deferredSave = new Deferred();
  private readonly deferredMounted = new Deferred();
  private readonly cls: new () => P;
  private readonly debouncedChangeState = debounce((changedValues: Partial<HostCfgItem>, _values) => {
    this.setState({ cfgInEdit: this.formRef.current.getFieldsValue(true) });
    const host = AbstractDevice.findKey(changedValues, 'host', []);
    if (host) {
      AbstractDevice.removeWhitespace(host.value, host.name, this.formRef.current);
    }
  }, Const.TIMEOUT_DEBOUNCE_SHORT);
  private vendorKey: string;
  private modelKey: string;

  protected abstract installUrl: string;
  protected readonly hwType: HwTypeKey;
  protected hwTypeFull: IHwTypeBase;
  protected readonly formRef = createRef<FormInstance<HostCfgItem<P>>>();
  protected readonly uniqueNameRule: Rule = {
    validateTrigger: ['onChange'],
    validator: GLOB.debounceAsync(
      (
        _rule: RuleObject,
        value: StoreValue,
        _callback: (error?: string) => void
      ): Promise<void | string> => {
        if (this.isEdit() && this.state.cfgInEdit.label === value) return Promise.resolve();
        if (this.getFilteredData().some((cfg) => cfg.label === value)) {
          const msg = `Name ${value} already used!`;
          Log.warn(msg);
          return Promise.reject(new Error(msg));
        }
        return Promise.resolve();
      },
      Const.TIMEOUT_DEBOUNCE_LONG
    ),
  };

  protected cannotAddReason: string = Label.LICENSE_LIMIT;
  readonly state: T;

  constructor(props, hwType: HwTypeKey, cls: new () => P) {
    super(props);
    this.saveCfg = this.saveCfg.bind(this);
    this.onFormFinishFailed = this.onFormFinishFailed.bind(this);
    this.state = {
      data: [],
      dialog: {},
      cfgInEdit: null,
      modalVisible: false,
      actionName: '',
      timestamp: null,
      saving: false,
      canAddDevice: false,
      proxyColumn: false,
      key: "xmHostCfgModal",
      useReset: false,
      export: {
        fileName: "devices_export",
        method: "POST",
        url: "/api/exporter/v1/values",
        body: {
          headers: ["Device", "Hostname", "Description"],
          values: this.mapValues([])
        }
      }

    } as T;
    this.hwType = hwType;
    this.cls = cls;
    this.init();
  }

  protected getFilteredData() {
    return this.state.data;
  }

  protected resetFilters(key: string) {
    this.setState(prev => ({
      ...prev,
      key: Math.random().toString(),
      useReset: false,
      export: { ...this.state.export, body: { ...this.state.export.body, values: this.mapValues(this.state.data) } }
    }));
    console.log(this.mapValues(this.state.data));
  }

  componentWillUnmount(): void {
    this.debouncedChangeState.cancel();
    this.deferredMounted.reject();
    this.abortCtrl.abort();
  }

  componentDidMount(): void {
    void GLOB.menuService.hwTypesLoaded.promise.then((types) => {
      this.hwTypeFull = types.find((ht) => ht.hw_type == this.hwType);
      void getVendors(this.hwTypeFull.class, this.abortCtrl.signal).then(response => {
        const vendors = response.data.data;
        for (const vendor of vendors) {
          const model = vendor.models?.find(m => m.hw_type === this.hwType);
          if (model) {
            this.vendorKey = vendor.vendor_key;
            this.modelKey = model.model_key;
            break;
          }
        }
      });
      return types;
    });
    this.deferredMounted.resolve();
  }

  componentDidUpdate(_prevProps: Readonly<unknown>, prevState: Readonly<PlatformState<P, S>>): void {
    if (prevState.data.length !== this.state.data.length) {
      this.setState(prev => ({ ...prev, proxyColumn: this.hasProxyColumn() }));
    }
  }

  private init() {
    void getHostCfgList(null, this.hwType).then((response) => {
      void this.deferredMounted.promise.then(() => {
        const data = response.data.data.map((cfg) => ({
          ...cfg,
          key: cfg.hostcfg_id,
        })) as HostCfgItem<P>[];
        this.setState({ data }, () => this.setState({ canAddDevice: this.canAddDevice(), proxyColumn: this.hasProxyColumn(), export: { ...this.state.export, body: { ...this.state.export.body, values: this.mapValues(data) } } }));
      });
    });
    void DataService.checkVersionReload();
  }

  private parseHosts(hostProperty): string[] {
    if (typeof hostProperty === 'string') return [hostProperty];
    else if (Array.isArray(hostProperty)) {
      return hostProperty.flatMap((child) => this.parseHosts(child));
    } else if (typeof hostProperty === 'object') {
      const hosts: string[] = [];
      for (const key in hostProperty) {
        if (
          Object.hasOwn(hostProperty, key) &&
          key.toLowerCase().includes('host')
        ) {
          const element = hostProperty[key];
          hosts.push(...this.parseHosts(element));
        }
      }
      return hosts;
    }
    return [];
  }

  protected canAddDevice() {
    const hwType = GLOB.menuService.hwTypes.find(ht => ht.hw_type === this.hwType);
    const clsLimit = useAppGlobStore.getState().info.backend.limits[hwType.class];
    if (!clsLimit) return true;
    const limit: number = clsLimit.limit || clsLimit.hwTypes[hwType.hw_type]?.limit;
    if (limit == null || limit < 0) return true;
    return limit > this.state.data.filter(hci => !hci.disabled && !hci.limited).length;
  }

  protected getSubTypeLimit(type?: string) {
    const hwType = GLOB.menuService.hwTypes.find(ht => ht.hw_type === this.hwType);
    const clsLimit = useAppGlobStore.getState().info.backend.limits[hwType.class];
    if (!clsLimit) return;
    const typeLimits = clsLimit.hwTypes[hwType.hw_type]?.types;
    if (!typeLimits) return;
    for (const key in typeLimits) {
      if (Object.hasOwn(typeLimits, key)) {
        const limit = typeLimits[key];
        if ((!type || type === key) && limit >= 0 && this.state.data.filter(cfg => cfg.data.licType === key).length >= limit)
          return { limit, hwType, key };
      }
    }
  }

  private hasProxyColumn() {
    return this.getFilteredData().filter(item => item.target).length > 0;
  }

  protected abstract getFormItems(cfg: HostCfgItem<P>): ReactNode;

  protected testCon(cfg: HostCfgItem<P>) {
    this.setState({ cfgInEdit: cfg, timestamp: Date.now() });
  }

  protected async deleteCfg(cfg: HostCfgItem<P>) {
    const promise = deleteHostCfg(cfg);
    void promise.then((_) => {
      this.setState(prev => ({ data: prev.data.filter((hci) => hci.hostcfg_id !== cfg.hostcfg_id) }));
      this.init();
      Log.info('Successfully deleted configuration ' + cfg.label);
    });
    return promise;
  }

  protected setIsModalOpen = (value: boolean) => {
    this.setState({ isModalOpen: value });
  };

  protected setRecord(cfg: HostCfgItem<P>) {
    this.setState({ record: cfg });
  }

  protected getCfgToSave() {
    return this.formRef.current.getFieldsValue(true) as HostCfgItem<P>;
  }

  protected saveCfg(_: HostCfgItem) {
    const cfg = this.getCfgToSave();
    cfg.custom_links = (cfg.custom_links ?? []).filter(link => link.label && link.url);
    if (cfg.hostcfg_id) {
      cfg.data.updated = Date.now();
      delete cfg.key;
      updateHostCfg(cfg).then(
        (_) => {
          //this.init()
          this.deferredSave.resolve();
          Log.info(`Configuration ${cfg.label} updated.`);
        },
        (_) => this.deferredSave.reject()
      );
    } else {
      cfg.hw_type = this.hwType;
      cfg.data.created = Date.now();
      cfg.data.updated = null;
      cfg.vendor_key = this.vendorKey;
      cfg.model_key = this.modelKey;
      createHostCfg(cfg).then(
        (response) => {
          //this.init()
          const created = response.data.data;
          this.deferredSave.resolve();
          Log.info(`Configuration ${created.label} created.`);
          AbstractDevice.newDataDialog();
        },
        (_) => this.deferredSave.reject()
      );
    }
  }

  protected onFormFinishFailed(error: ValidateErrorEntity) {
    Log.error('Form contains error!', error);
    this.deferredSave.reject();
  }

  protected prepareCfgForModal(cfg: HostCfgItem<P>) {
    return cfg;
  }

  protected showModalForm(cfg: HostCfgItem<P>, actionName: string) {
    const prepared = this.prepareCfgForModal(cfg);
    const cfgInEdit = prepared.custom_links?.length ? prepared : { ...prepared, custom_links: [] };

    this.setState({
      cfgInEdit,
      modalVisible: true,
      actionName,
    });
  }

  protected path(selector: (props: HostCfgItem<P>) => unknown) {
    return nameof<HostCfgItem<P>>(selector).split('.') as NamePath<HostCfgItem<P>>;
  }

  protected isEdit() {
    return !!this.state.cfgInEdit?.hostcfg_id;
  }

  protected getPort(label: string, name: NamePath, props?: FormItemProps) {
    return AbstractDevice.getPort(label, name, props);
  }

  protected protoSelect() {
    return AbstractDevice.protoSelect();
  }

  protected commonCard(aliasLabel = 'Host alias') {
    return (
      <Card
        {...HostCfgCardProps}
        title='Common options'
        extra={
          <Form.Item
            labelCol={{ span: 19 }}
            wrapperCol={{ span: 5 }}
            label='Disabled'
            name={this.path((t) => t.disabled)}
            valuePropName='checked'
            style={{ marginLeft: '1em', marginBottom: 0 }}
          >
            <Checkbox />
          </Form.Item>
        }
      >
        <Form.Item
          label={aliasLabel}
          name={this.path((t) => t.label)}
          rules={[{ required: true, message: 'Please input host alias!' }, this.uniqueNameRule, GLOB.TitleCharactersValidatorRule]}
        >
          <Input />
        </Form.Item>

        <Form.Item label='Description' name={this.path((t) => t.data.description)}>
          <Input />
        </Form.Item>

        <Form.Item label='Management URL' name={this.path((t) => t.data.managementUrl)} rules={[{ pattern: GLOB.URL_PATTERN, message: "Management URL is not valid" }]}>
          <Input type='url' />
        </Form.Item>
      </Card>
    );
  }

  protected getNewDialogState(): S {
    return {} as S;
  }

  protected getNewData() {
    return new this.cls();
  }

  protected setUseReset = (value: boolean) => {
    this.setState({ useReset: value });
  };

  protected mapValues = (values: HostCfgItem[]) => {
    return values.map((cfg) => {
      const hosts = this.parseHosts(cfg.data);
      return [
        cfg.label,
        hosts.join(', '),
        cfg.data.description ? cfg.data.description : "",
      ];
    });
  };

  protected onChange: GridProps<HostCfgItem<P>>['onChange'] = (_p, filters, _s, extra) => {
    if (filters) {
      for (const key in filters) {
        if (filters[key]?.length !== null) {
          this.setUseReset(true);
          break;
        }
      }
    }
    this.setState({ export: { ...this.state.export, body: { ...this.state.export.body, values: this.mapValues(extra.currentDataSource) } } });
  };

  render(): ReactNode {
    return (
      <div className='xm-host-cfg xm-host-cfg-lpar'>
        <div className='xm-host-cfg-buttons'>
          <Button
            disabled={!GLOB.isFreeOrExpiredTrial() && !this.state.canAddDevice}
            title={this.state.canAddDevice ? null : this.cannotAddReason}
            onClick={(_) => {
              if (GLOB.isFreeOrExpiredTrial() && !this.state.canAddDevice) {
                AbstractDevice.freeLicenseLimitDialog();
                return;
              }

              const hwType = GLOB.menuService.hwTypes.find(ht => ht.hw_type === this.hwType);
              const clsLimit = useAppGlobStore.getState().info.backend.limits[hwType.class];
              if (clsLimit) {
                let softLimit = clsLimit.softLimit;
                if (!softLimit?.exceeded) {
                  softLimit = clsLimit.hwTypes[hwType.hw_type]?.softLimit;
                }
                if (softLimit?.exceeded) {
                  AbstractDevice.softLimitDialog(softLimit);
                  return;
                }
              }

              this.setState({ dialog: this.getNewDialogState() });
              const cfg = new HostCfgItem<P>();
              cfg.data = this.getNewData();
              this.showModalForm(cfg, 'New');
            }}
          >
            Add new device
          </Button>
          <Button
            style={{ marginLeft: '0.5em' }}
            disabled={this.state.data.length === 0}
            onClick={() => this.setState({ isConnTestOpen: true })}
          >
            Connection test all
          </Button>
          <XmTableUtil
            useReset={true}
            useExport={true}
            exportableTypes={{ PDF: false }}
            export={this.state.export}
            setTableKey={(key: string) => { this.resetFilters(key); }}
            size={"medium"}
            activeReset={this.state.useReset}
          />
        </div>
        <TableConfiguration<P>
          columns={(this.state.proxyColumn ? this.columns : this.columns.filter(value => value.key !== "proxy"))}
          dataSource={this.getFilteredData()}
          subTypeLimit={this.getSubTypeLimit()}
          onChange={(p, f, s, e) => { this.onChange(p, f, s, e); }}
          topOffset={130}
          _key={this.state.key}
        />
        <XmModal
          className='xm-hostcfg-modal'
          mask={XmModal.MASK_NOT_CLOSABLE}
          open={this.state.modalVisible}
          onCancel={() => this.setState({ modalVisible: false })}
          onOk={() => {
            this.setState({ saving: true });
            this.deferredSave.reset();
            void this.deferredSave.promise.then(() => {
              this.setState({ modalVisible: false });
              this.init();
            }).finally(() => this.setState({ saving: false }));
            this.formRef.current.submit();
          }}
          okButtonProps={{ loading: this.state.saving }}
          okText='Save'
          destroyOnHidden={true}
          title={<div>
            {this.state.actionName} {this.hwTypeFull?.label} configuration
            <XmHelp title='Installation instructions' style={{ float: 'right', marginRight: '30px' }} href={this.installUrl} />
          </div>}
        >
          <Form
            ref={this.formRef}
            className='xm-form xm-form-space'
            style={{ minHeight: '20em' }}
            initialValues={this.state.cfgInEdit}
            onFinish={(values) => this.saveCfg(values)}
            onFinishFailed={(errorInfo) => this.onFormFinishFailed(errorInfo)}
            onValuesChange={this.debouncedChangeState}
            validateMessages={FormValidateMessages}
            {...BasicFormProps}
          >
            {this.getFormItems(this.state.cfgInEdit)}

            {AbstractDevice.getIgnoreHScard(this.state.cfgInEdit)}
            <Card {...HostCfgCardProps} title="Custom links" extra={<XmHelp href="https://xormon.com/external-links.php" />} styles={{ body: { paddingLeft: '1em' } }} >
              <CustomLinksFormList name="custom_links" required={false} />
            </Card>
          </Form>
        </XmModal>
        <AdvancedSettingsCFG isModalOpen={this.state.isModalOpen} record={this.state.record} setIsModalOpen={this.setIsModalOpen} />
        <ConnectionTestModal cfg={this.state.cfgInEdit} timestamp={this.state.timestamp} />
        <XmModal
          open={this.state.isConnTestOpen}
          destroyOnHidden={true}
          title="Connection Test"
          onCancel={() => this.setState({ isConnTestOpen: false })}
          onOk={() => this.setState({ isConnTestOpen: false })}
          okText="Close"
          cancelButtonProps={{ style: { display: 'none' } }}
          width="50em"
        >
          <ImportConnectionTest vendors={[]} devices={this.state.data?.filter(d => !d.target && !d.disabled && !d.limited)} disableOnFail={false} />
        </XmModal>
      </div>
    );
  }
}
